import React from 'react';
import styled from 'styled';
import { DateTime } from 'luxon';
import { theme, ThemeProvider } from 'styled';
import { space } from 'helpers/style';
import { DateRangeLabel, DateTimeLabel } from './subcomponents/date-label';
import { PlaybackControls } from './subcomponents/playback';
import { Autopilot } from './subcomponents/camera';
import { DensityMapControlsContext } from './context';
import { Clock } from '../clock';

export type DensityMapControlsProps = {
  isPlaying: boolean;
  isRepeating: boolean;
  autopilotIsOn: boolean;
  dateRange: {
    from: DateTime;
    to: DateTime;
  };
  currentTime?: DateTime;
  clock: Clock;
  playbackSpeed: number;
  baseSpeed: number;
  eventHandler?: (e: CustomEvent) => void;
};

type Subcomponents = {
  DateTimeLabel: typeof DateTimeLabel;
  DateRangeLabel:  typeof DateRangeLabel;
  PlaybackControls: typeof PlaybackControls;
  Autopilot: typeof Autopilot;
}

const StyledPanel = styled.section`
  &::after {
    transition: width var(--transition);
    display: block;
    position: absolute;
    content: '';
    top: 0;
    left: 0;
    width: 0;
    width: var(--progress);
    height: 5px;
    background-color: ${({ theme }) => theme.color.primary};
    box-shadow: inset 2px 2px 2px rgba(0, 0, 0, 0.5);
  }

  width: 100%;
  background-color: rgb(250, 250, 250);
  position: relative;
  font-size: ${({ theme }) => theme.font.size.small};

  
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  grid-template-columns: 1fr auto 1fr;

  --border-style: 1px dashed ${({ theme }) => theme.color.grayL60};

  > * {

    padding: ${space(3)} ${space(3)};
    display: flex;
    flex-grow: 1;
    justify-content: center;
    align-items: center;

    border-right: var(--border-style);
    &:last-child { border-right: none }
  }
`;


const DensityMapControls: React.FC<DensityMapControlsProps> & Subcomponents = props => {

  const progressValueProp = {
    ['--progress']: `${props.clock.position * 100}%`,
    ['--transition']: `${(props.baseSpeed / props.playbackSpeed) * 1.5}ms`
  } as React.CSSProperties;

  /**
   * Theme provider required because this component is
   * injected into the maptalks.Map's controls layer.
   */
  return (
    <ThemeProvider theme={theme}>
      <StyledPanel style={progressValueProp}>
        <DensityMapControlsContext.Provider value={{...props}}>
          {props.children}
        </DensityMapControlsContext.Provider>
      </StyledPanel>
    </ThemeProvider>
  );
};

DensityMapControls.PlaybackControls = PlaybackControls;
DensityMapControls.DateRangeLabel = DateRangeLabel;
DensityMapControls.DateTimeLabel = DateTimeLabel;
DensityMapControls.Autopilot = Autopilot;


export { DensityMapControls };
